"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import { formatPrice } from "@/lib/utils";

interface Chip {
  /** Уникальный id для key (вместе с label). */
  id: string;
  label: string;
  /** Обработчик «×» — возвращает новые searchParams. */
  remove: () => URLSearchParams;
}

interface Props {
  chips: Chip[];
}

/**
 * Горизонтальная плашка с активными фильтрами. Каждый — чипс «×»,
 * клик удаляет соответствующий ключ из URL и перерендеривает листинг.
 */
function AppliedBar({ chips }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const [pending, startTransition] = useTransition();

  if (chips.length === 0) return null;

  const onRemove = (chip: Chip) => {
    const next = chip.remove();
    startTransition(() => {
      router.push(`${pathname}?${next.toString()}`, { scroll: false });
    });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onRemove(c)}
          disabled={pending}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-3 py-1 text-xs font-medium text-brand-900 transition-colors hover:bg-brand-200 disabled:opacity-60"
        >
          <span>{c.label}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </button>
      ))}
    </div>
  );
}

/**
 * Билдер чипсов из текущих searchParams. Использовать в client component'е
 * на странице категории.
 */
export function AppliedFiltersBar() {
  const params = useSearchParams();
  const chips: Chip[] = [];

  // 1. Цена
  const pmin = params.get("price_min");
  const pmax = params.get("price_max");
  if (pmin || pmax) {
    const label =
      pmin && pmax
        ? `Цена: ${formatPrice(pmin)} – ${formatPrice(pmax)}`
        : pmin
        ? `от ${formatPrice(pmin)}`
        : `до ${formatPrice(pmax!)}`;
    chips.push({
      id: "price",
      label,
      remove: () => {
        const next = new URLSearchParams(params.toString());
        next.delete("price_min");
        next.delete("price_max");
        return next;
      },
    });
  }

  // 2. Фасеты — все `f=…`
  const fAll = params.getAll("f");
  fAll.forEach((raw, idx) => {
    const sep = raw.indexOf("|");
    if (sep <= 0) return;
    const key = raw.slice(0, sep);
    const val = raw.slice(sep + 1);
    let label = `${key}: ${val.replace("..", " – ")}`;
    if (label.length > 60) label = label.slice(0, 57) + "…";
    chips.push({
      id: `f-${idx}-${key}`,
      label,
      remove: () => {
        const next = new URLSearchParams();
        for (const [k, v] of params.entries()) {
          if (k === "f" && v === raw) continue;
          next.append(k, v);
        }
        return next;
      },
    });
  });

  if (chips.length === 0) return null;
  return <AppliedBar chips={chips} />;
}
